/**
 * GovUtilityBillDetailModal — click a bill in Recent entries to open this.
 *
 * Shows the bill's full details + GL + Bank movement, and exposes the
 * Document Lifecycle actions (Void / Reversing Entry / Replace) that
 * were wired to `services/document_lifecycle` in Wave P-B.8.6.
 *
 * NOTE on flow:
 *   • Void      = full undo (annuls bill + cascades reversal to GL + Bank).
 *   • Reverse   = same as Void but keeps the bill in "POSTED" state and
 *                 posts a mirror reversing journal (used when the period
 *                 is closed and Void is not allowed).
 *   • Replace   = out-of-scope for this quick modal; opens a hint toast.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import {
  Loader2, AlertTriangle, Undo2, XCircle, ShieldCheck,
  Ban, Wallet, FileText, Briefcase, Link2,
} from 'lucide-react';
import { axiosInstance } from '../App';


const fmtSAR = (n) => `${Number(n || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})} SAR`;


const stateBadge = (s) => {
  const map = {
    DRAFT:     ['bg-slate-100 text-slate-700 border-slate-300', 'Draft'],
    POSTED:    ['bg-blue-100 text-blue-700 border-blue-300', 'Posted'],
    PAID:      ['bg-emerald-100 text-emerald-700 border-emerald-300', 'Paid'],
    VOIDED:    ['bg-rose-100 text-rose-700 border-rose-300', 'Voided'],
    REPLACED:  ['bg-amber-100 text-amber-700 border-amber-300', 'Replaced'],
    REVERSED:  ['bg-purple-100 text-purple-700 border-purple-300', 'Reversed'],
  };
  const [cls, label] = map[s] || ['bg-slate-100 text-slate-700 border-slate-300', s || '—'];
  return (
    <span className={`inline-block text-[10px] uppercase tracking-wider
                     border rounded px-1.5 py-0.5 font-semibold ${cls}`}>
      {label}
    </span>
  );
};


export default function GovUtilityBillDetailModal({ open, onClose, bill, onChanged }) {
  const [planLoading, setPlanLoading] = useState(false);
  const [voidPlan, setVoidPlan]       = useState(null);
  const [action, setAction]           = useState(null); // "void" | "reverse"
  const [reason, setReason]           = useState('');
  const [busy, setBusy]               = useState(false);
  const [projects, setProjects]       = useState([]);
  const [linkPid, setLinkPid]         = useState('');
  const [linking, setLinking]         = useState(false);
  const [currentProject, setCurrentProject] = useState(null);

  useEffect(() => {
    // Reset internal state whenever a different bill opens.
    setVoidPlan(null);
    setAction(null);
    setReason('');
    setLinkPid(bill?.project_id || '');
    setCurrentProject(bill?.project_name || null);
  }, [bill?.id]);

  useEffect(() => {
    if (!open) return;
    axiosInstance.get('/projects')
      .then((r) => setProjects(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [open]);

  if (!bill) return null;

  const isTerminal = ['VOIDED', 'REPLACED', 'REVERSED'].includes(bill.lifecycle_state);

  const linkProject = async () => {
    setLinking(true);
    try {
      const r = await axiosInstance.post(`/supplier-bills/${bill.id}/link-project`,
        { project_id: linkPid || null });
      const p = projects.find((x) => x.id === linkPid);
      setCurrentProject(linkPid ? (p?.name || null) : null);
      toast.success(linkPid
        ? `تم ربط الفاتورة بالمشروع${r.data?.reclass_journal_no ? ` — قيد إعادة تبويب ${r.data.reclass_journal_no}` : ''}`
        : 'تم فك ربط الفاتورة من المشروع');
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message_ar || (typeof d === 'string' ? d : 'فشل ربط المشروع'));
    } finally {
      setLinking(false);
    }
  };

  const loadPlan = async (op) => {
    setPlanLoading(true);
    setVoidPlan(null);
    try {
      const r = await axiosInstance.get(
        `/gov-utility-payments/${bill.id}/lifecycle/plan`,
        { params: { operation: op === 'reverse' ? 'reversing_entry' : 'void' } });
      setVoidPlan(r.data);
      setAction(op);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unable to build plan.');
    } finally {
      setPlanLoading(false);
    }
  };

  const confirmAction = async () => {
    if ((reason || '').trim().length < 5) {
      toast.error('Please enter a reason (min 5 characters).');
      return;
    }
    setBusy(true);
    try {
      const url = action === 'reverse'
        ? `/gov-utility-payments/${bill.id}/reversing-entry`
        : `/gov-utility-payments/${bill.id}/void`;
      await axiosInstance.post(url, { reason: reason.trim() });
      toast.success(
        action === 'reverse'
          ? 'Reversing entry posted successfully.'
          : 'Bill voided. GL & Bank Ledger reversed.'
      );
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="gu-bill-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <FileText size={18} className="text-indigo-600" />
            <span className="font-mono">{bill.bill_number}</span>
            {stateBadge(bill.lifecycle_state)}
            {bill.status === 'paid' && (
              <span className="inline-block text-[10px] uppercase
                              bg-emerald-100 text-emerald-700 border border-emerald-300
                              px-1.5 py-0.5 rounded font-semibold">
                Paid
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* ── Summary grid ─────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-[10px] uppercase text-slate-500">Date</div>
            <div className="font-medium">{bill.bill_date}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">Provider</div>
            <div className="font-medium">{bill.supplier_name}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">Reference</div>
            <div className="font-medium text-xs" dir="ltr">
              {bill.gov_utility?.reference_value || '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">Base</div>
            <div className="font-medium tabular-nums">{fmtSAR(bill.subtotal)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">VAT</div>
            <div className="font-medium tabular-nums">{fmtSAR(bill.vat_amount)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">Total</div>
            <div className="font-bold text-slate-800 tabular-nums">
              {fmtSAR(bill.total)}
            </div>
          </div>
        </div>

        {/* ── GL & Bank ────────────────────────────────────────────── */}
        <div className="border-t border-slate-200 pt-3 grid md:grid-cols-2 gap-3">
          <div className="border border-slate-200 rounded p-3">
            <div className="text-[10px] uppercase text-slate-500 mb-1
                            flex items-center gap-1">
              <ShieldCheck size={11} /> GL Journal
            </div>
            {bill.journal_no ? (
              <div className="font-mono text-emerald-700">{bill.journal_no}</div>
            ) : (
              <div className="text-amber-600 text-xs inline-flex items-center gap-1">
                <AlertTriangle size={11} /> Not posted yet — run Backfill
              </div>
            )}
          </div>
          <div className="border border-slate-200 rounded p-3">
            <div className="text-[10px] uppercase text-slate-500 mb-1
                            flex items-center gap-1">
              <Wallet size={11} /> Bank Ledger
            </div>
            {bill.bank_account_id ? (
              <div className="text-slate-700 text-xs">
                Paid from <b>{bill.bank_account_id}</b>
                {bill.bank_transaction_id && (
                  <div className="font-mono text-[11px] text-slate-500">
                    Txn: {bill.bank_transaction_id}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-slate-500 text-xs">— (unpaid / AP obligation)</div>
            )}
          </div>
        </div>

        {/* ── Project link — retro-link without re-creating ────────── */}
        <div className="border-t border-slate-200 pt-3">
          <div className="text-[10px] uppercase text-slate-500 mb-2
                          flex items-center gap-1">
            <Briefcase size={11} /> ربط بمشروع / Project Link
          </div>
          {currentProject && (
            <div className="text-xs mb-2" data-testid="gu-current-project">
              مرتبطة حالياً بـ:{' '}
              <span className="inline-block bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full font-medium">
                {currentProject}
              </span>
            </div>
          )}
          {isTerminal ? (
            <div className="text-xs text-slate-500">لا يمكن ربط فاتورة ملغاة/معكوسة بمشروع.</div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <select
                data-testid="gu-link-project-select"
                value={linkPid}
                onChange={(e) => setLinkPid(e.target.value)}
                className="border border-slate-300 rounded px-2 py-1.5 text-sm min-w-[220px] bg-white">
                <option value="">— بدون مشروع (فك الربط) —</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <Button
                size="sm"
                variant="outline"
                disabled={linking}
                onClick={linkProject}
                data-testid="gu-link-project-btn">
                {linking ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
                &nbsp;حفظ الربط
              </Button>
            </div>
          )}
          <p className="text-[11px] text-slate-400 mt-1.5">
            الربط يعيد وسم قيود الفاتورة بالمشروع وينشئ قيد إعادة تبويب إلى تكلفة المشاريع تلقائياً — المبالغ لا تتغير.
          </p>
        </div>

        {/* ── Lifecycle Actions ────────────────────────────────────── */}
        <div className="border-t border-slate-200 pt-4">
          <div className="text-[10px] uppercase tracking-wider
                          text-slate-500 mb-2">
            Lifecycle Actions
          </div>

          {isTerminal ? (
            <div className="text-xs text-slate-500 bg-slate-50
                            border border-slate-200 rounded p-3">
              This bill is in state <b>{bill.lifecycle_state}</b> and cannot
              be modified further. Create a new bill if a replacement is
              needed.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="destructive"
                size="sm"
                disabled={planLoading || busy}
                onClick={() => loadPlan('void')}
                data-testid="gu-void-btn">
                {planLoading && action !== 'reverse'
                  ? <Loader2 size={14} className="animate-spin" />
                  : <Ban size={14} />}
                &nbsp;Void Bill
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={planLoading || busy}
                onClick={() => loadPlan('reverse')}
                data-testid="gu-reverse-btn">
                {planLoading && action === 'reverse'
                  ? <Loader2 size={14} className="animate-spin" />
                  : <Undo2 size={14} />}
                &nbsp;Reversing Entry
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled
                title="Use Void, then re-create with the correct data."
                data-testid="gu-replace-btn">
                Replace (create new after Void)
              </Button>
            </div>
          )}

          {voidPlan && (
            <div className="mt-4 border-2 border-amber-300 bg-amber-50
                            rounded p-3 space-y-2"
                 data-testid="gu-lifecycle-plan">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertTriangle size={14} />
                <span className="font-bold text-sm">
                  Plan · {action === 'reverse' ? 'Reversing Entry' : 'Void'}
                </span>
              </div>
              <ul className="list-disc list-inside text-xs text-slate-700 space-y-0.5">
                {(voidPlan.steps || []).map((step, i) => (
                  <li key={i}>{step.description || step.action || JSON.stringify(step)}</li>
                ))}
                {(!voidPlan.steps || voidPlan.steps.length === 0) && (
                  <li>No visible sub-steps — the operation will proceed.</li>
                )}
              </ul>

              {voidPlan.requires_reversing_entry && (
                <div className="text-[11px] text-rose-700 bg-rose-50
                                border border-rose-200 rounded p-2">
                  🔒 The bill is in a locked period. A direct <b>Void</b> is
                  not allowed — use <b>Reversing Entry</b> instead.
                </div>
              )}

              <div className="pt-2">
                <label className="block text-[11px] font-semibold text-slate-600
                                  uppercase tracking-wider mb-1">
                  Reason (min 5 characters)
                </label>
                <Textarea
                  data-testid="gu-lifecycle-reason"
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Amount entered incorrectly — voiding to recreate with the right total." />
              </div>

              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={busy || (reason || '').trim().length < 5}
                  onClick={confirmAction}
                  data-testid="gu-lifecycle-confirm">
                  {busy
                    ? <Loader2 size={14} className="animate-spin" />
                    : <XCircle size={14} />}
                  &nbsp;Confirm {action === 'reverse' ? 'Reversing Entry' : 'Void'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => { setVoidPlan(null); setReason(''); setAction(null); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
